import { useCallback } from 'react';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { getFullAPIFileURL } from '../../../../../common/getFullAPIFileURL';
import { setPromoteMediaDraft, type PromoteMediaDraft } from '../../../draftStore';
import { inlineMediaUrl } from '../../../inlineMediaUrl';
import { useRollout } from './RolloutContext';
import type { PlanStepView } from './rolloutModel';
import { fileHasWatermark } from '../../../../watermarkUpsell/files/watermarkedFile';

/** "9:16" of a width and a height, or the pixel size; empty when not known. */
function aspectOf(width: number | null, height: number | null): string {
  if (width === null || height === null) {
    return '';
  }
  if (width === height) {
    return '1:1';
  }
  if (width * 16 === height * 9) {
    return '9:16';
  }
  if (width * 5 === height * 4) {
    return '4:5';
  }
  return `${width}x${height}`;
}

/**
 * "Open in Composer" of a step (design rollout-plan c1): the post of the open channel tab when it
 * has one (`?postUuid=`), else a new post with the file of the step, its release and its channels
 * and format pre-filled (the media handover of `draftStore`).
 */
export function useOpenStepInComposer(): (step: PlanStepView, platform: string | null) => void {
  const router = useSMLRouter();
  const { item, release } = useRollout();

  return useCallback(
    (step: PlanStepView, platform: string | null) => {
      const post = step.channels.find((channel) => channel.platform === platform)?.post ?? null;
      if (post !== null) {
        router.push(SMLRoutes.promoteComposer, { postUuid: post.uuid });
        return;
      }
      const uuid = step.day.asset?.assetUuid ?? null;
      const song = {
        title: item.title,
        artistName: item.artistName,
        catalogItemUuid: item.uuid,
        smartLinkId: item.smartLinkId ?? undefined,
        releaseDate: item.releaseDate ?? undefined,
        platforms: step.channels.map((channel) => channel.platform),
        format: step.day.format === 'story' ? ('story' as const) : ('post' as const),
      };
      let draft: PromoteMediaDraft | null = null;
      const asset = [...(release.ready ?? []), ...release.assets].find((one) => one.uuid === uuid);
      const reel = release.reels.find((one) => one.uuid === uuid);
      if (asset !== undefined && asset.fileId !== null && asset.fileUrl !== null) {
        draft = {
          ...song,
          fileId: asset.fileId,
          fileUrl: getFullAPIFileURL(asset.fileUrl),
          thumbnailUrl: asset.posterUrl === null ? undefined : inlineMediaUrl(asset.posterUrl),
          kind: asset.durationSec === null ? 'image' : 'video',
          aspect: aspectOf(asset.width, asset.height),
          width: asset.width,
          height: asset.height,
          durationSec: asset.durationSec,
          renderedAt: asset.renderedAt ?? asset.createdAt,
          themeId: asset.themeId,
          watermark: fileHasWatermark(asset),
        };
      } else if (reel !== undefined && reel.video !== null) {
        draft = {
          ...song,
          fileId: reel.video.fileId,
          fileUrl: getFullAPIFileURL(reel.video.url),
          thumbnailUrl: reel.video.posterUrl === null ? undefined : inlineMediaUrl(reel.video.posterUrl),
          kind: 'video',
          aspect: '9:16',
          durationSec: reel.durationSec,
          renderedAt: reel.createdAt,
          themeId: 'aiReel',
          watermark: fileHasWatermark(reel),
        };
      }
      if (draft !== null) {
        setPromoteMediaDraft(draft);
      }
      router.push(SMLRoutes.promoteComposer);
    },
    [item, release, router]
  );
}
